/**
 * DRBFM Custom Hooks
 *
 * React hooks for managing DRBFM analysis workflow:
 * - useDRBFM: Main hook for full DRBFM workflow
 * - useDRBFMSessions: Hook for session list management
 * - useDRBFMAnalysis: Hook for analysis execution with SSE
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from './redux';
import {
  // Selectors
  selectCurrentSession,
  selectWizardStep,
  selectInputData,
  selectAnalysisProgress,
  selectAnalysisResults,
  selectIsExporting,
  selectExportError,
  selectCanStartAnalysis,
  selectSessionsLoading,
  selectSessionsError,
  // Actions
  setCurrentSession,
  setWizardStep,
  setInputData,
  replaceInputData,
  setAnalysisError,
  setAnalysisResults,
  resetWizard,
  startExport,
  completeExport,
  setExportError,
} from '../features/drbfm/drbfmSlice';
import {
  drbfmService,
  CreateSessionParams,
} from '../services/drbfm.service';
import {
  DRBFMInputData,
  DRBFMWizardStep,
} from '../types';
import { resolveApiErrorMessage } from '../utils/apiErrorMessages';
import { useDRBFMAnalysis } from './useDRBFMAnalysis';
import { analysisRequestFromInput } from '../services/drbfm.analysis';

export { useDRBFMSessions } from './useDRBFMSessions';
export { useDRBFMAnalysis } from './useDRBFMAnalysis';

// ==============================================================================
// useDRBFM - Main Hook for Full DRBFM Workflow
// ==============================================================================

export const useDRBFM = (options: { initializeWorkspace?: boolean } = {}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { user } = useAppSelector((state) => state.auth);

  // Selectors
  const currentSession = useAppSelector(selectCurrentSession);
  const wizardStep = useAppSelector(selectWizardStep);
  const inputData = useAppSelector(selectInputData);
  const analysisProgress = useAppSelector(selectAnalysisProgress);
  const analysisResults = useAppSelector(selectAnalysisResults);
  const isExporting = useAppSelector(selectIsExporting);
  const exportError = useAppSelector(selectExportError);
  const canStartAnalysis = useAppSelector(selectCanStartAnalysis);
  const sessionsLoading = useAppSelector(selectSessionsLoading);
  const sessionsError = useAppSelector(selectSessionsError);

  // Local state
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const creation = useRef<symbol | null>(null);
  useEffect(() => {
    setIsCreatingSession(false);
    return () => { creation.current = null; };
  }, [user?.id, user?.tenantId]);

  // Use sub-hooks
  const { runAnalysisRequest, cancel: cancelRunningAnalysis, isRunning, completionPending,
    retryCompletion, discardCompletion, clearSourceError } = useDRBFMAnalysis();

  const cancelAnalysis = useCallback(() => {
    creation.current = null;
    setIsCreatingSession(false);
    cancelRunningAnalysis();
  }, [cancelRunningAnalysis]);

  // Create a new session and start analysis
  const createSessionAndAnalyze = useCallback(async (submittedInput?: DRBFMInputData) => {
    if (creation.current || isRunning || completionPending) return null;
    if (!user) {
      dispatch(setAnalysisError(t('errors.authenticationRequired')));
      return null;
    }

    // A form may submit before Redux has rendered its latest draft.
    const submitted = submittedInput ? { title: '', ...submittedInput } : inputData;
    let request;
    try {
      request = analysisRequestFromInput(submitted);
    } catch {
      dispatch(setAnalysisError(t('drbfm.errors.allFieldsRequired')));
      return null;
    }

    const attempt = Symbol();
    creation.current = attempt;
    setIsCreatingSession(true);
    if (submittedInput) dispatch(replaceInputData(submitted));

    try {
      const pair = request.sources?.pattern === 'old_new_documents' ? request.sources : undefined;
      const params: CreateSessionParams = {
        itemName: submitted.itemName,
        title: submitted.title,
        specOldDocumentId: pair?.oldDocumentId,
        specNewDocumentId: pair?.newDocumentId,
      };

      const response = await drbfmService.createSession(params);
      if (creation.current !== attempt) return null;

      // Get full session details
      const session = await drbfmService.getSession(response.id);
      if (creation.current !== attempt) return null;
      dispatch(setCurrentSession(session));

      // Start analysis
      const completed = await runAnalysisRequest(session.id, request, options.initializeWorkspace);
      return completed ? session : null;
    } catch (err) {
      if (creation.current !== attempt) return null;
      dispatch(setAnalysisError(
        resolveApiErrorMessage(t, err, { fallbackKey: 'drbfm.errors.createFailed' }),
      ));
      return null;
    } finally {
      if (creation.current === attempt) {
        creation.current = null;
        setIsCreatingSession(false);
      }
    }
  }, [user, inputData, dispatch, runAnalysisRequest, t, isRunning,
    completionPending, options.initializeWorkspace]);

  // Load an existing session
  const loadSession = useCallback(
    async (sessionId: string) => {
      try {
        const session = await drbfmService.getSession(sessionId);
        dispatch(setCurrentSession(session));

        // If session is completed, load results
        if (session.status === 'completed') {
          const results = await drbfmService.getSessionResults(sessionId);
          dispatch(setAnalysisResults(results));
          dispatch(setWizardStep('results'));
        } else if (session.status === 'draft') {
          dispatch(setWizardStep('input'));
        }

        return session;
      } catch (err) {
        dispatch(setAnalysisError(
          resolveApiErrorMessage(t, err, { fallbackKey: 'drbfm.errors.sessionLoadFailed' }),
        ));
        return null;
      }
    },
    [dispatch, t]
  );

  // Resume analysis for an existing session
  const resumeAnalysis = useCallback(
    async (sessionId: string) => {
      let request;
      try {
        request = analysisRequestFromInput(inputData);
      } catch {
        dispatch(setAnalysisError(t('drbfm.errors.specsRequiredToResume')));
        return;
      }

      await runAnalysisRequest(sessionId, request, options.initializeWorkspace);
    },
    [inputData, runAnalysisRequest, dispatch, t, options.initializeWorkspace]
  );

  // Update input data
  const updateInput = useCallback(
    (data: Partial<DRBFMInputData>) => {
      if (isRunning) return;
      discardCompletion();
      dispatch(setInputData(data));
    },
    [dispatch, discardCompletion, isRunning]
  );

  // Navigate to wizard step
  const goToStep = useCallback(
    (step: DRBFMWizardStep) => {
      dispatch(setWizardStep(step));
    },
    [dispatch]
  );

  // Reset the wizard
  const reset = useCallback(() => {
    cancelAnalysis();
    dispatch(resetWizard());
  }, [dispatch, cancelAnalysis]);

  // Export results as Excel, matching the history list action.
  const exportExcel = useCallback(async () => {
    if (!currentSession) {
      dispatch(setExportError(t('drbfm.errors.noSessionToExport')));
      return;
    }

    dispatch(startExport());

    try {
      const filename = `drbfm_${currentSession.itemName}_${currentSession.id.slice(0, 8)}.xlsx`;
      await drbfmService.downloadExportedExcel(currentSession.id, filename);
      dispatch(completeExport());
    } catch (err) {
      dispatch(setExportError(
        resolveApiErrorMessage(t, err, { fallbackKey: 'drbfm.errors.exportFailed' }),
      ));
    }
  }, [currentSession, dispatch, t]);

  return {
    // State
    currentSession,
    wizardStep,
    inputData,
    analysisProgress,
    analysisResults,
    isExporting,
    exportError,
    canStartAnalysis,
    isLoading: sessionsLoading || isCreatingSession,
    isAnalyzing: isRunning || analysisProgress.isRunning,
    completionPending,
    error: sessionsError || analysisProgress.error,

    // Actions
    createSessionAndAnalyze,
    retryCompletion,
    clearSourceError,
    loadSession,
    resumeAnalysis,
    cancelAnalysis,
    updateInput,
    goToStep,
    reset,
    exportExcel,
  };
};

// ==============================================================================
// Default Export
// ==============================================================================

export default useDRBFM;
